import { Button } from '@shared/components/ui/button';
import { useGoogleLogin, useGoogleOAuth } from '@react-oauth/google';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLoginWithGoogle } from '../hooks/useAuth';

interface GoogleLoginButtonProps {
  onSuccess?: () => void;
}

/** Evita montar hooks de Google sin clientId (tiran el ErrorBoundary del login). */
export const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({ onSuccess }) => {
  const { t } = useTranslation('auth');
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    return (
      <p className="text-sm text-destructive" data-testid="google-login-missing-client-id">
        {t(
          'errors.googleClientIdMissing',
          'Login con Google no configurado (falta VITE_GOOGLE_CLIENT_ID). Usa correo y contraseña.',
        )}
      </p>
    );
  }

  return <GoogleLoginButtonReady onSuccess={onSuccess} />;
};

const GoogleLoginButtonReady: React.FC<GoogleLoginButtonProps> = ({ onSuccess }) => {
  const { t } = useTranslation('auth');
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const loginWithGoogleMutation = useLoginWithGoogle();

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    scope: 'email profile openid',
    onSuccess: async (tokenResponse) => {
      try {
        await loginWithGoogleMutation.mutateAsync(tokenResponse.access_token);
        onSuccess?.();
      } catch (error) {
        console.error('Error en login con Google:', error);
      }
    },
  });

  return (
    <div className="space-y-2">
      {loginWithGoogleMutation.error && (
        <p className="text-sm text-destructive">
          {(loginWithGoogleMutation.error as Error).message ||
            t('errors.genericError', 'Ha ocurrido un error. Inténtalo de nuevo.')}
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        fullWidth
        disabled={loginWithGoogleMutation.isPending || !scriptLoadedSuccessfully}
        onClick={() => googleLogin()}
        data-testid="google-login-button"
      >
        {loginWithGoogleMutation.isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('login.googleLoading', 'Verificando con Google...')}
          </>
        ) : (
          t('login.googleButton', 'Continuar con Google')
        )}
      </Button>
    </div>
  );
};

export default GoogleLoginButton;
